// Packages
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

// Components
import { EntLayoutSideNav, INavItem } from '@ent-ui-kit/react';

// Hooks
import { useAnalytics } from 'hooks/useAnalytics';
import { useAuthSession } from 'hooks/useAuthSession';
import { useAuthUser } from 'hooks/useAuthUser';
import { useRouteUtils } from '../../hooks/useRouteUtils';

// Types
import { navItems } from './navItems';

// Utils
import { currentPageTitle } from 'contexts/utils/analyticsUtils';
import { getClientInfo } from '../../utils/generalUtils';
import { commonAnalytics } from 'utils/commonAnalytics';

interface IProps {
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
}

const Layout = ({ children, className = '', loading }: IProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthSession();
  const { authUser } = useAuthUser();
  const { checkRoutePermissions } = useRouteUtils();

  const { trackButtonEvent } = useAnalytics();
  const { signOutBtn } = commonAnalytics.userMenuLogout.buttonClicks!;

  const handleNavItemClick = (item: INavItem) => {
    navigate(item.path);
  };

  const handleFooterLinkClick = useCallback(
    (label: string): void => {
      trackButtonEvent(
        {
          itemClicked: label,
          pageRegion: 'Footer Navigation',
        },
        {
          siteSection: currentPageTitle(),
        }
      );
    },
    [trackButtonEvent]
  );

  const handleSignOutClick = useCallback(async () => {
    trackButtonEvent(signOutBtn, {
      siteSection: currentPageTitle(),
    });
    await signOut();
  }, [signOut, signOutBtn, trackButtonEvent]);

  useEffect(() => {
    const clientInfo = getClientInfo();

    if (clientInfo.os === 'Windows') {
      // Add windows class to body alongside mountaineer to fix MS Windows specific UI bugs (like very wide scrollbars).
      document.getElementsByTagName('body')[0].classList.add('windows');
    }
  }, []);

  const filteredNavItems = useMemo(() => {
    return navItems.map((navItem) => ({
      ...navItem,
      items: navItem.items.filter((item) => {
        if (!item.permissions) {
          return true;
        }

        return checkRoutePermissions(item.permissions);
      }),
    }));
  }, [checkRoutePermissions]);

  const userMenu = useMemo(
    () => ({
      userName: `${authUser?.firstName} ${authUser?.lastName}`,
      organizationName: authUser?.pid || '',
      menuItems: [
        {
          label: 'Profile',
          path: '/profile',
          onClick: () => navigate('/profile'),
        },
      ],
      onSignOut: handleSignOutClick,
    }),
    [authUser, navigate, handleSignOutClick]
  );

  return (
    <EntLayoutSideNav
      className={classNames({
        layout: true,
        [`${className}`]: !!className,
      })}
      title="Ops Center"
      isLoading={loading}
      navItems={filteredNavItems}
      activeNavPath={location.pathname}
      onNavItemClick={handleNavItemClick}
      onFooterLinkClick={handleFooterLinkClick}
      userMenu={userMenu}
    >
      {children}
    </EntLayoutSideNav>
  );
};

export default Layout;
